import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminConfiguration } from '../../hooks/use-admin-configuration';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import styled from 'styled-components';
import Variables from '../../design-system/variables';
import Colours from '../../design-system/colours';
import { ConfigurationSettingsDataUtils } from '../../common/utils/services/configuration-settings-data-utils';
import OvConfigurationSettingItem, {
  ConfigurationOption,
} from '../UI/organisms/OvConfigurationSettingItem';
import { ConfigurationSettingsFields } from '../../firebase/document-field.enums';
import Snackbar from '@mui/material/Snackbar';
import UpdateIcon from '@mui/icons-material/Update';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import breakpoints from '../../design-system/breakpoints';
import useAxios from 'axios-hooks';
import { ADMIN_SCANS_ROUTE } from '../../api/backend';

const ConfigurationSystem: React.FC = () => {
  const { t } = useTranslation();
  const {
    latestConfiguration,
    isLoading,
    error,
    updateConfiguration,
    clearConfigurationCache,
  } = useAdminConfiguration();
  const errorMessage = error ? getAxiosErrorMessage(error) : '';

  const [mappedConfigs, setMappedConfigs] = useState<ConfigurationOption[]>([]);
  const [openErrorMessageSnackbar, setOpenErrorMessageSnackbar] =
    useState(false);

  useEffect(() => {
    if (latestConfiguration) {
      setMappedConfigs(
        ConfigurationSettingsDataUtils.mapConfigurationsForUI(
          latestConfiguration
        )
      );
    }
  }, [latestConfiguration]);

  useEffect(() => {
    if (errorMessage) {
      setOpenErrorMessageSnackbar(true);
    }
  }, [errorMessage]);

  const [{ data: toolkitVersions, loading: isToolkitVersionsLoading }] =
    useAxios<string[]>(
      {
        url: `/${ADMIN_SCANS_ROUTE}/toolkit-versions`,
        method: 'get',
      },
      { useCache: false }
    );

  const onConfigurationValueChanged = (
    fieldName: ConfigurationSettingsFields,
    value: any
  ) => {
    const modifiedConfigs = mappedConfigs.map((config) => {
      if (config.fieldName === fieldName) {
        return { ...config, value };
      }

      return config;
    });

    setMappedConfigs(modifiedConfigs);
  };

  const onConfigurationUpdate = () => {
    if (!latestConfiguration) {
      return;
    }

    const updatedConfigs = mappedConfigs.reduce(
      (acc, config) => ({ ...acc, [config.fieldName as string]: config.value }),
      {}
    );

    updateConfiguration.mutate({ ...latestConfiguration, ...updatedConfigs });
  };

  const onConfigurationClearCache = () => {
    clearConfigurationCache.mutate();
  };

  return (
    <Container>
      {mappedConfigs &&
        mappedConfigs.map((config, index) => (
          <OvConfigurationSettingItem
            key={index}
            options={config}
            onChange={onConfigurationValueChanged}
            isDisabled={isLoading}
          />
        ))}

      {!isToolkitVersionsLoading && (toolkitVersions?.length ?? 0) > 0 && (
        <ToolkitInfoSection>
          <h4>{t('configurationSettings.availableToolkitVersions')}</h4>
          <ul>
            {(toolkitVersions ?? []).map((version, idx) => (
              <li key={idx}>{version}</li>
            ))}
          </ul>
        </ToolkitInfoSection>
      )}

      <ActionsContainer>
        <OvCompactButton onClick={onConfigurationUpdate} icon={<UpdateIcon />}>
          {t('configurationSettings.update')}
        </OvCompactButton>
        <OvCompactButton
          onClick={onConfigurationClearCache}
          icon={<DeleteOutline />}
        >
          {t('configurationSettings.clearCache')}
        </OvCompactButton>
      </ActionsContainer>

      {errorMessage && <OvErrorMessage message={errorMessage} />}
      {isLoading && <OvLoadingIndicator position="fixed" />}

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={openErrorMessageSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenErrorMessageSnackbar(false)}
      >
        <ErrorMessageContainer>
          <OvErrorMessage message={errorMessage} />
        </ErrorMessageContainer>
      </Snackbar>
    </Container>
  );
};

export default ConfigurationSystem;

const Container = styled.div`
  margin-left: 1rem;
  box-shadow: ${Variables.boxShadow.defaultBox};
  background-color: ${Colours.WHITE};
  padding: 1.5rem;
  border-radius: ${Variables.borderRadius.LARGE};
`;

const ErrorMessageContainer = styled.div`
  padding: 1rem;
  border-radius: ${Variables.borderRadius.LARGE};
  background-color: ${Colours.STRAWBERRY['50']};
`;

const ActionsContainer = styled.div`
  flex-grow: 1;
  min-width: 13rem;
  display: flex;
  flex-wrap: 'wrap';
  gap: 1rem;
  justify-content: flex-start;
  @media (min-width: ${breakpoints.md}) {
    min-width: auto;
  }
`;

const ToolkitInfoSection = styled.div`
  margin: 0 0 2rem 0;
  padding: 0rem;
  border-radius: ${Variables.borderRadius.MEDIUM};
  background-color: ${Colours.GRAY['10']};
`;
